
// ============================================
// src/features/dashboard/services/dashboardApi.ts
// ============================================

import { fetchUtils } from 'react-admin';
import {
  DashboardSummaryResponse,
  UsageTrendsResponse,
  ZonePerformanceResponse,
  AssignmentEfficiencyResponse,
} from '../types';

// Función helper para obtener el token
const getAuthToken = (): string | null => {
  // Primero intentar obtener el token del localStorage
  const token = localStorage.getItem('token');
  if (token) {
    return token;
  }
  
  // Si no está ahí, intentar con el authProvider de react-admin
  const auth = localStorage.getItem('auth');
  if (auth) {
    try {
      const authData = JSON.parse(auth);
      return authData.token || authData.access_token;
    } catch {
      // Ignore parsing errors
    }
  }
  
  return null;
};

// Cliente HTTP con autenticación
const httpClient = (url: string, options: any = {}) => {
  const token = getAuthToken();
  
  if (!token) {
    return Promise.reject(new Error('No authentication token found'));
  }
  
  if (!options.headers) {
    options.headers = new Headers({ Accept: 'application/json' });
  }
  
  // Añadir el token Bearer
  options.headers.set('Authorization', `Bearer ${token}`);
  
  return fetchUtils.fetchJson(url, options);
};

// URL base de la API - ajusta según tu configuración
const API_BASE_URL = process.env.REACT_APP_API_URL;

// Construir URLs completas
const buildUrl = (endpoint: string): string => {
  // Asegurarse de que no haya dobles barras
  const baseUrl = API_BASE_URL.replace(/\/$/, '');
  const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${baseUrl}${path}`;
};

export const dashboardApi = {
  /**
   * Obtener resumen del dashboard
   * GET /api/v1/ui/reports/dashboard-summary
   */
  getDashboardSummary: async (hotelId?: string, period: string = 'today'): Promise<DashboardSummaryResponse> => {
    const params = new URLSearchParams();
    if (hotelId) {
      params.append('hotelId', hotelId);
    }
    params.append('period', period);
    
    const url = buildUrl(`/api/v1/ui/reports/dashboard-summary?${params.toString()}`);
  
    
    try {
      const response = await httpClient(url);
      return response.json;
    } catch (error) {
      console.error('Error fetching dashboard summary:', error);
      throw error;
    }
  },

  /**
   * Obtener tendencias de uso
   * GET /api/v1/ui/reports/usage-trends
   */
  getUsageTrends: async (hotelId?: string, from?: Date, to?: Date): Promise<UsageTrendsResponse> => {
    const params = new URLSearchParams();
    if (hotelId) {
      params.append('hotelId', hotelId);
    }
    if (from) {
      params.append('from', from.toISOString());
    }
    if (to) {
      params.append('to', to.toISOString());
    }
    
    const url = buildUrl(`/api/v1/ui/reports/usage-trends?${params.toString()}`);

    
    try {
      const response = await httpClient(url);
      return response.json;
    } catch (error) {
      console.error('Error fetching usage trends:', error);
      throw error;
    }
  },

  /**
   * Obtener rendimiento por zona
   * GET /api/v1/ui/reports/zone-performance
   */
  getZonePerformance: async (hotelId?: string): Promise<ZonePerformanceResponse> => {
    const params = new URLSearchParams();
    if (hotelId) {
      params.append('hotelId', hotelId);
    }
    
    const url = buildUrl(`/api/v1/ui/reports/zone-performance?${params.toString()}`);

    
    try {
      const response = await httpClient(url);
      return response.json;
    } catch (error) {
      console.error('Error fetching zone performance:', error);
      throw error;
    }
  },

  /**
   * Obtener eficiencia de asignaciones
   * GET /api/v1/ui/reports/assignment-efficiency
   */
  getAssignmentEfficiency: async (hotelId?: string, period: string = 'last_30_days'): Promise<AssignmentEfficiencyResponse> => {
    const params = new URLSearchParams();
    if (hotelId) {
      params.append('hotelId', hotelId);
    }
    params.append('period', period);
    
    const url = buildUrl(`/api/v1/ui/reports/assignment-efficiency?${params.toString()}`);

    
    try {
      const response = await httpClient(url);
      return response.json;
    } catch (error) {
      console.error('Error fetching assignment efficiency:', error);
      throw error;
    }
  },
};

// ============================================
// ALTERNATIVA CON FETCH NATIVO (más control sobre errores)
// src/features/dashboard/services/dashboardApiNative.ts
// ============================================

class DashboardApiService {
  private baseUrl: string;
  
  constructor() {
    // Usar la URL de tu API real
    this.baseUrl = process.env.REACT_APP_API_URL;
  }

  private getAuthToken(): string | null {
    // Intentar múltiples lugares donde podría estar el token
    const token = localStorage.getItem('token');
    if (token) return token;

    const auth = localStorage.getItem('auth');
    if (auth) {
      try {
        const authData = JSON.parse(auth);
        return authData.token || authData.access_token;
      } catch {
        // Ignore
      }
    }

    // También chequear sessionStorage si usas eso
    const sessionToken = sessionStorage.getItem('token');
    if (sessionToken) return sessionToken;

    return null;
  }

  private getAuthHeaders(): Headers {
    const headers = new Headers({
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    });
    
    const token = this.getAuthToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    
    return headers;
  }

  private async fetchWithAuth(url: string, options: RequestInit = {}): Promise<any> {
    const fullUrl = `${this.baseUrl}${url}`;

    
    const response = await fetch(fullUrl, {
      ...options,
      headers: this.getAuthHeaders(),
    });



    if (!response.ok) {
      if (response.status === 401) {
        // Token expirado o inválido
        console.error('Authentication failed - redirecting to login');
        localStorage.removeItem('token');
        localStorage.removeItem('auth');
        window.location.href = '#/login'; // Para react-admin
        throw new Error('Authentication required');
      }
      
      if (response.status === 404) {
        console.error(`Endpoint not found: ${fullUrl}`);
        throw new Error(`Endpoint not found: ${url}`);
      }
      
      // Intentar obtener el mensaje de error del servidor
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch {
        // Si no se puede parsear el error, usar el mensaje por defecto
      }
      
      throw new Error(errorMessage);
    }

    return response.json();
  }

  async getDashboardSummary(hotelId?: string, period: string = 'today'): Promise<DashboardSummaryResponse> {
    const params = new URLSearchParams();
    if (hotelId) params.append('hotelId', hotelId);
    params.append('period', period);
    
    return this.fetchWithAuth(`/reports/dashboard-summary?${params}`);
  }

  async getUsageTrends(hotelId?: string, from?: Date, to?: Date): Promise<UsageTrendsResponse> {
    const params = new URLSearchParams();
    if (hotelId) params.append('hotelId', hotelId);
    if (from) params.append('from', from.toISOString());
    if (to) params.append('to', to.toISOString());

    return this.fetchWithAuth(`/reports/usage-trends?${params}`);
  }

  async getZonePerformance(hotelId?: string): Promise<ZonePerformanceResponse> {
    const params = new URLSearchParams();
    if (hotelId) params.append('hotelId', hotelId);
    
    return this.fetchWithAuth(`/reports/zone-performance?${params}`);
  }

  async getAssignmentEfficiency(hotelId?: string, period: string = 'last_30_days'): Promise<AssignmentEfficiencyResponse> {
    const params = new URLSearchParams();
    if (hotelId) params.append('hotelId', hotelId);
    params.append('period', period);
    
    return this.fetchWithAuth(`/reports/assignment-efficiency?${params}`);
  }
}

// Exportar una instancia única
export const dashboardApiService = new DashboardApiService();

